import "./style.css";

import {
  EnvironmentConfiguration,
  ErrorScreen,
  IRenderer,
  SpawnConfiguration,
} from "@mml-io/3d-web-client-core";
import {
  CreateRendererOptions,
  Networked3dWebExperienceClient,
} from "@mml-io/3d-web-experience-client";
import {
  WorldConfigUpdateBroadcastType,
  WorldRenderConfig,
  WorldRenderWorldConfig,
  WorldUpdatableConfig,
} from "@private/worlds-api";

import airAnimationFileUrl from "./assets/models/anim_air.glb";
import doubleJumpAnimationFileUrl from "./assets/models/anim_double_jump.glb";
import idleAnimationFileUrl from "./assets/models/anim_idle.glb";
import jogAnimationFileUrl from "./assets/models/anim_jog.glb";
import sprintAnimationFileUrl from "./assets/models/anim_run.glb";

declare global {
  interface Window {
    WORLD_RENDER_CONFIG: WorldRenderConfig;
  }
}

function showError(errorMessage: string) {
  document.body.appendChild(new ErrorScreen("Error", errorMessage).element);
}

function toEnvironmentConfiguration(
  data: WorldRenderWorldConfig["environmentConfiguration"],
): EnvironmentConfiguration {
  const hdrJpgUrl: string | undefined = data?.skybox?.hdrJpgUrl;
  const hdrUrl: string | undefined = data?.skybox?.hdrUrl;
  let skybox: EnvironmentConfiguration["skybox"];
  if (hdrJpgUrl) {
    skybox = {
      hdrJpgUrl,
    };
  } else if (hdrUrl) {
    skybox = {
      hdrUrl,
    };
  } else {
    skybox = undefined;
  }

  return {
    groundPlane: data.groundPlane,
    skybox,
    envMap: {
      intensity: data?.envMap?.intensity ?? undefined,
    },
    sun: {
      intensity: data?.sun?.intensity ?? undefined,
      polarAngle: data?.sun?.polarAngle ?? undefined,
      azimuthalAngle: data?.sun?.azimuthalAngle ?? undefined,
    },
    fog: {
      fogNear: data?.fog?.fogNear ?? undefined,
      fogFar: data?.fog?.fogFar ?? undefined,
    },
    ambientLight: {
      intensity: data?.ambientLight?.intensity ?? undefined,
    },
  };
}

function toSpawnConfiguration(
  data: WorldRenderWorldConfig["spawnConfiguration"],
): SpawnConfiguration {
  return {
    spawnPosition: {
      x: data?.spawnPosition?.x ?? undefined,
      y: data?.spawnPosition?.y ?? undefined,
      z: data?.spawnPosition?.z ?? undefined,
    },
    spawnPositionVariance: {
      x: data?.spawnPositionVariance?.x ?? undefined,
      y: data?.spawnPositionVariance?.y ?? undefined,
      z: data?.spawnPositionVariance?.z ?? undefined,
    },
    spawnYRotation: data?.spawnYRotation ?? undefined,
    respawnTrigger: {
      minX: data?.respawnTrigger?.minX ?? undefined,
      maxX: data?.respawnTrigger?.maxX ?? undefined,
      minY: data?.respawnTrigger?.minY ?? undefined,
      maxY: data?.respawnTrigger?.maxY ?? undefined,
      minZ: data?.respawnTrigger?.minZ ?? undefined,
      maxZ: data?.respawnTrigger?.maxZ ?? undefined,
    },
    enableRespawnButton: data?.enableRespawnButton ?? false,
  };
}

async function loadWorld(worldConfig: WorldRenderWorldConfig) {
  const enableTweakPane = !!worldConfig.enableTweakPane;
  const allowOrbitalCamera = !!worldConfig.allowOrbitalCamera;
  const environmentConfiguration = worldConfig.environmentConfiguration;
  const spawnConfiguration = worldConfig.spawnConfiguration;
  const avatarConfiguration = worldConfig.avatarConfiguration;
  const loadingConfiguration = worldConfig.loadingScreen;

  /*
   If the current url contains a "token" query param then remove it and replace
   the current history so that the token is not leaked if the user shares the url.
  */
  if (window.location.search.includes("token")) {
    const url = new URL(window.location.href);
    url.searchParams.delete("token");
    window.history.replaceState({}, document.title, url.toString());
  }

  const holder = Networked3dWebExperienceClient.createFullscreenHolder();

  let createRenderer:
    | ((options: CreateRendererOptions) => IRenderer)
    | undefined;
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get("webgpu") === "true") {
    const { NarwhalWorldRendererWrapper } =
      await import("@msquared-internal/3d-web-narwhal");
    createRenderer = (options: CreateRendererOptions) => {
      return new NarwhalWorldRendererWrapper(options);
    };
  }

  const app = new Networked3dWebExperienceClient(holder, {
    sessionToken: worldConfig.sessionToken,
    authToken: worldConfig.authToken ?? null,
    userNetworkAddress: worldConfig.networkUrl,
    allowCustomDisplayName:
      worldConfig.displayNameConfiguration?.allowCustomDisplayNames ?? false,
    enableChat: worldConfig.enableChat ?? false,
    animationConfig: {
      airAnimationFileUrl,
      idleAnimationFileUrl,
      jogAnimationFileUrl,
      sprintAnimationFileUrl,
      doubleJumpAnimationFileUrl,
    },
    mmlDocuments: worldConfig.mmlDocuments,
    allowOrbitalCamera,
    enableTweakPane,
    environmentConfiguration: toEnvironmentConfiguration(
      environmentConfiguration,
    ),
    spawnConfiguration: toSpawnConfiguration(spawnConfiguration),
    avatarConfiguration,
    loadingScreen: loadingConfiguration,
    createRenderer,
    onServerBroadcast: (broadcast: { broadcastType: string; payload: any }) => {
      if (broadcast.broadcastType === WorldConfigUpdateBroadcastType) {
        const updatePayload = broadcast.payload as {
          world: WorldUpdatableConfig;
        };
        app.updateConfig({
          ...updatePayload.world,
          environmentConfiguration: toEnvironmentConfiguration(
            updatePayload.world.environmentConfiguration ?? {},
          ),
          spawnConfiguration: toSpawnConfiguration(
            updatePayload.world.spawnConfiguration ?? {},
          ),
          allowCustomDisplayName:
            updatePayload.world.displayNameConfiguration
              ?.allowCustomDisplayNames ?? false,
        });
      }
    },
  });
  app.update();
}

window.addEventListener("DOMContentLoaded", () => {
  if (!window.WORLD_RENDER_CONFIG) {
    showError("WORLD_RENDER_CONFIG not found");
  } else {
    if (window.WORLD_RENDER_CONFIG.type === "error") {
      showError(window.WORLD_RENDER_CONFIG.message);
    } else if (window.WORLD_RENDER_CONFIG.type === "worldConfig") {
      loadWorld(window.WORLD_RENDER_CONFIG);
    } else {
      showError(`Invalid WORLD_RENDER_CONFIG type`);
    }
  }
});
